import { useEffect, useRef, useState } from 'react';
import { useT } from '../i18n';
import { loadTurnstileScript, turnstileBroker, type TurnstileApi } from '../lib/turnstile';

export interface TurnstileHostProps {
  siteKey?: string;
  /** Tests inject a fake loader; the default injects Cloudflare's script. */
  load?: () => Promise<TurnstileApi>;
}

/**
 * The funnel's one Turnstile widget for the guarded API calls (lib/turnstile/broker.ts), in the
 * shell. Invisible unless Cloudflare wants an interaction; then a small panel at the bottom of
 * the column holds it. Renders nothing without a site key.
 */
export function TurnstileHost({
  siteKey = turnstileBroker.siteKey,
  load = loadTurnstileScript,
}: TurnstileHostProps) {
  const t = useT();
  const box = useRef<HTMLDivElement>(null);
  const [asking, setAsking] = useState(false);

  useEffect(() => {
    if (!siteKey) return;
    let gone = false;
    let api: TurnstileApi | null = null;
    let id: string | undefined;
    load()
      .then((ts) => {
        if (gone || !box.current) return;
        api = ts;
        id = ts.render(box.current, {
          sitekey: siteKey,
          action: 'api_guard',
          appearance: 'interaction-only',
          size: 'flexible',
          'refresh-expired': 'auto',
          callback: (token: string) => {
            setAsking(false);
            turnstileBroker.issued(token);
          },
          'expired-callback': () => turnstileBroker.expired(),
          'before-interactive-callback': () => setAsking(true),
          'after-interactive-callback': () => setAsking(false),
        });
        if (id !== undefined) turnstileBroker.attach({ api: ts, id });
      })
      .catch(() => turnstileBroker.failedToLoad());
    return () => {
      gone = true;
      if (id !== undefined) {
        turnstileBroker.detach(id);
        api?.remove(id);
      }
    };
  }, [siteKey, load]);

  if (!siteKey) return null;
  return (
    <div className={asking ? 'turnstile-host turnstile-host--asking' : 'turnstile-host'} role="status">
      {asking && <p className="turnstile-host__note">{t('common.turnstileAsk')}</p>}
      <div ref={box} data-testid="turnstile-host" />
    </div>
  );
}
